'use client';

import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import type { Drone } from '@/types/fleet';

/**
 * The COP's drone registry, keyed by drone id.
 *
 * The live WebSocket stream carries telemetry only — position, battery, link —
 * and nothing about the airframe itself. Model, serial, firmware, payloads and
 * the home dock all live on the registry row, so any surface that answers "what
 * is this aircraft" (as opposed to "what is it doing") needs this join.
 *
 * Failure is surfaced rather than swallowed, following the same
 * per-resource-flag convention as useCopContextData: the detail panels degrade
 * to "unavailable" while the map keeps working off telemetry alone.
 */
export function useDroneRegistry(): {
  droneRegistry: Map<string, Drone>;
  droneRegistryFailed: boolean;
  droneRegistryLoading: boolean;
} {
  const [droneRegistry, setDroneRegistry] = useState<Map<string, Drone>>(new Map());
  const [droneRegistryFailed, setDroneRegistryFailed] = useState(false);
  // Starts true: the fetch is issued on mount, so "empty and not yet failed" is
  // "still loading", never "the fleet is empty". Surfaces have to be able to tell
  // those apart - the dispatch picker states one of them as fact to an operator.
  const [droneRegistryLoading, setDroneRegistryLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    api
      .get<{ data: Drone[] }>('/api/drones')
      .then((r) => {
        if (cancelled) return;
        setDroneRegistry(new Map(r.data.map((d) => [d.id, d])));
        setDroneRegistryFailed(false);
      })
      .catch(() => {
        if (cancelled) return;
        setDroneRegistryFailed(true);
        console.warn('Failed to load drone registry for COP');
      })
      .finally(() => {
        if (cancelled) return;
        setDroneRegistryLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return { droneRegistry, droneRegistryFailed, droneRegistryLoading };
}
